/**
 * Bridge between baked terrain presets and the runtime heightfield renderer.
 *
 * The terrain pipeline emits a DEM heightmap plus compiled semantic rasters:
 * `terrain_surface.r8` is the baked L1/L2 surface classification consumed by
 * shaders and CPU systems directly. L3/L4 are loaded on `TerrainAsset` as
 * feature JSON; this module only needs the surface raster for DEM carving and
 * terrain material masks.
 */

import type { DEMFormat, DEMManifest } from "./dem-loader";
import { loadDEM } from "./dem-loader";
import type { TerrainHeightmap } from "./procedural-terrain";
import type { TerrainMetaJson, TerrainSurface } from "../assets/TerrainAsset";
import { SURFACE_CODE } from "../assets/TerrainAsset";

export interface TerrainPreset {
  id: string;
  name: string;
  /** Folder under public/terrain/, ending in '/'. */
  url: string;
}

export interface LoadedPreset {
  preset: TerrainPreset;
  meta: TerrainMetaJson;
  heightmap: TerrainHeightmap;
  /** Baked L1/L2 surface classification, one byte per texel. */
  classification: {
    data: Uint8Array;
    width: number;
    height: number;
  };
  /** Smooth road coverage texture: R=road. */
  roadCoverage: {
    data: Uint8Array;
    width: number;
    height: number;
  };
  /**
   * R8 mask used to softly fade terrain/water into the background at the
   * polygon boundary. 0 = fully outside polygon (discard), 255 = well
   * inside. Smooth gradient over ~PADDING_FEATHER_M metres at the edge.
   */
  polygonFade: {
    data: Uint8Array;
    width: number;
    height: number;
  };
}

export const DEFAULT_TERRAIN_PRESET: TerrainPreset = {
  id: "zielona_zatoka",
  name: "Zielona Zatoka (Mazury)",
  url: "terrain/zielona_zatoka/",
};

interface TerrainPresetIndexJson {
  terrains: TerrainPreset[];
}

interface ClassifiedMask {
  classification: Uint8Array;
  waterFootprintMask: Uint8Array;
  landMask: Uint8Array;
  width: number;
  height: number;
}

const DEFAULT_CLASSIFIED_WATER_DEPTH_M = 3.0;
const SHORE_BLEND_M = 4.0;
const LAND_FLOOR_M = 0.3;

const LAND_CLASS_SURFACES = new Set<TerrainSurface>([
  "forest",
  "meadow",
  "wetland",
  "building",
  "parking",
  "road",
  "major_road",
  "bridge",
  "dock",
  "railway",
]);

export async function loadTerrainPresetList(
  baseUrl = "terrain/",
): Promise<TerrainPreset[]> {
  try {
    const root = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
    const res = await fetch(`${root}index.json`);
    if (!res.ok) return [DEFAULT_TERRAIN_PRESET];
    const json = (await res.json()) as TerrainPresetIndexJson;
    if (!Array.isArray(json.terrains) || json.terrains.length === 0) {
      return [DEFAULT_TERRAIN_PRESET];
    }
    return json.terrains.map((t) => ({
      id: t.id,
      name: t.name,
      url: t.url,
    }));
  } catch {
    return [DEFAULT_TERRAIN_PRESET];
  }
}

export async function fetchTerrainMeta(url: string): Promise<TerrainMetaJson> {
  const dir = url.endsWith("/") ? url : `${url}/`;
  const res = await fetch(`${dir}terrain_meta.json`);
  if (!res.ok) {
    throw new Error(
      `terrain-preset-loader: missing ${dir}terrain_meta.json (${res.status})`,
    );
  }
  return (await res.json()) as TerrainMetaJson;
}

export function manifestFromMeta(
  preset: TerrainPreset,
  meta: TerrainMetaJson,
): DEMManifest {
  const dir = preset.url.endsWith("/") ? preset.url : `${preset.url}/`;
  const range = meta.elevation_range_m;
  const minOffset = meta.elevation_min_m - meta.water_level_m;
  const scale = range / 65535;
  return {
    url: `${dir}heightmap.png`,
    format: "png16" as DEMFormat,
    resolution: meta.output_size,
    extent: Math.max(meta.width_m, meta.height_m),
    scale,
    offset: minOffset,
  };
}

function padToSquare(
  hm: TerrainHeightmap,
  meta: TerrainMetaJson,
): TerrainHeightmap {
  const wM = meta.width_m;
  const hM = meta.height_m;
  if (Math.abs(wM - hM) < 0.5) return hm;

  const extent = Math.max(wM, hM);
  const targetRes = hm.resolution;
  const out = new Float32Array(targetRes * targetRes);
  const srcOriginX = (extent - wM) * 0.5;
  const srcOriginZ = (extent - hM) * 0.5;
  const cellM = extent / (targetRes - 1);

  for (let j = 0; j < targetRes; j++) {
    const z = j * cellM;
    const sz = (z - srcOriginZ) / hM;
    for (let i = 0; i < targetRes; i++) {
      const x = i * cellM;
      const sx = (x - srcOriginX) / wM;
      if (sx < 0 || sx > 1 || sz < 0 || sz > 1) {
        out[j * targetRes + i] = 0;
        continue;
      }
      const fi = sx * (hm.resolution - 1);
      const fj = sz * (hm.resolution - 1);
      const i0 = Math.floor(fi);
      const j0 = Math.floor(fj);
      const i1 = Math.min(i0 + 1, hm.resolution - 1);
      const j1 = Math.min(j0 + 1, hm.resolution - 1);
      const tx = fi - i0;
      const ty = fj - j0;
      const a = hm.data[j0 * hm.resolution + i0]!;
      const b = hm.data[j0 * hm.resolution + i1]!;
      const c = hm.data[j1 * hm.resolution + i0]!;
      const d = hm.data[j1 * hm.resolution + i1]!;
      const ab = a + (b - a) * tx;
      const cd = c + (d - c) * tx;
      out[j * targetRes + i] = ab + (cd - ab) * ty;
    }
  }
  return { data: out, resolution: targetRes, extent };
}

export async function loadTerrainPreset(
  preset: TerrainPreset,
): Promise<LoadedPreset> {
  const meta = await fetchTerrainMeta(preset.url);
  const manifest = manifestFromMeta(preset, meta);
  const raw = await loadDEM(manifest);
  const padded = padToSquare(raw, meta);
  const carved = await applyCompiledSurface(padded, preset, meta);
  return {
    preset,
    meta,
    heightmap: carved.heightmap,
    classification: carved.classification,
    roadCoverage: carved.roadCoverage,
    polygonFade: carved.polygonFade,
  };
}

async function loadCompiledSurfaceMask(
  dir: string,
  meta: TerrainMetaJson,
): Promise<ClassifiedMask> {
  const surface = meta.compiled_surface;
  if (!surface) {
    throw new Error(
      "terrain-preset-loader: terrain_meta.json is missing compiled_surface",
    );
  }
  if (surface.format !== "r8") {
    throw new Error(
      `terrain-preset-loader: unsupported compiled_surface format ${surface.format}`,
    );
  }

  const res = await fetch(`${dir}${surface.file}`);
  if (!res.ok) {
    throw new Error(
      `terrain-preset-loader: missing ${dir}${surface.file} (${res.status})`,
    );
  }
  const classification = new Uint8Array(await res.arrayBuffer());
  const expected = surface.width * surface.height;
  if (classification.length !== expected) {
    throw new Error(
      `terrain-preset-loader: ${surface.file} has ${classification.length} byte(s), expected ${expected}`,
    );
  }

  const waterFootprintMask = new Uint8Array(expected);
  const landMask = new Uint8Array(expected);
  for (let i = 0; i < classification.length; i++) {
    const code = classification[i]!;
    if (code === SURFACE_CODE.water) {
      waterFootprintMask[i] = 1;
    } else if (isLandSurfaceCode(code)) {
      landMask[i] = 1;
    }
  }

  return {
    classification,
    waterFootprintMask,
    landMask,
    width: surface.width,
    height: surface.height,
  };
}

function isLandSurfaceCode(code: number): boolean {
  for (const surface of LAND_CLASS_SURFACES) {
    if (SURFACE_CODE[surface] === code) return true;
  }
  return false;
}

async function applyCompiledSurface(
  hm: TerrainHeightmap,
  preset: TerrainPreset,
  meta: TerrainMetaJson,
): Promise<{
  heightmap: TerrainHeightmap;
  classification: { data: Uint8Array; width: number; height: number };
  roadCoverage: { data: Uint8Array; width: number; height: number };
  polygonFade: { data: Uint8Array; width: number; height: number };
}> {
  const dir = preset.url.endsWith("/") ? preset.url : `${preset.url}/`;
  const classified = await loadCompiledSurfaceMask(dir, meta);
  const ow = classified.width;
  const oh = classified.height;
  const classification = classified.classification;
  const mask = new Uint8Array(classified.waterFootprintMask);
  const classifiedLand = classified.landMask;
  const dist = chamfer34(mask, ow, oh);
  const cellM = Math.max(meta.width_m, meta.height_m) / Math.max(ow, oh);
  const classifiedWaterDepthM =
    meta.water_type === "sea"
      ? (meta.max_depth_m ?? DEFAULT_CLASSIFIED_WATER_DEPTH_M)
      : DEFAULT_CLASSIFIED_WATER_DEPTH_M;

  const extent = hm.extent;
  const wM = meta.width_m;
  const hM = meta.height_m;
  const srcOriginX = (extent - wM) * 0.5;
  const srcOriginZ = (extent - hM) * 0.5;
  const cellHm = extent / (hm.resolution - 1);
  const out = new Float32Array(hm.data);

  for (let j = 0; j < hm.resolution; j++) {
    const z = j * cellHm;
    const sz = (z - srcOriginZ) / hM;
    if (sz < 0 || sz > 1) continue;
    const oy = Math.min(oh - 1, Math.floor(sz * oh));
    for (let i = 0; i < hm.resolution; i++) {
      const x = i * cellHm;
      const sx = (x - srcOriginX) / wM;
      if (sx < 0 || sx > 1) continue;
      const ox = Math.min(ow - 1, Math.floor(sx * ow));
      const oIdx = oy * ow + ox;
      const idx = j * hm.resolution + i;
      const land = out[idx]!;

      if (classifiedLand[oIdx]) {
        if (land < LAND_FLOOR_M) out[idx] = LAND_FLOOR_M;
        continue;
      }

      const dM = dist[oIdx]! * cellM;
      if (dM >= SHORE_BLEND_M) {
        if (land < LAND_FLOOR_M) out[idx] = LAND_FLOOR_M;
        continue;
      }
      const t = dM / SHORE_BLEND_M;
      const target = -classifiedWaterDepthM;
      const k = t * t * (3 - 2 * t);
      out[idx] = target * (1 - k) + land * k;
    }
  }

  const squareSize = Math.max(ow, oh);
  // Pre-fill with SURF_OUTSIDE so the padded strips that square-up a
  // non-square bbox are treated as "outside polygon" by terrain/water
  // shaders (which discard SURF_OUTSIDE fragments).
  const sqClass = new Uint8Array(squareSize * squareSize).fill(
    SURFACE_CODE.outside,
  );
  for (let j = 0; j < squareSize; j++) {
    const worldZ = ((j + 0.5) / squareSize) * extent;
    const srcV = (worldZ - srcOriginZ) / hM;
    if (srcV < 0 || srcV > 1) continue;
    const srcRow = Math.min(oh - 1, Math.floor(srcV * oh));
    for (let i = 0; i < squareSize; i++) {
      const worldX = ((i + 0.5) / squareSize) * extent;
      const srcU = (worldX - srcOriginX) / wM;
      if (srcU < 0 || srcU > 1) continue;
      const srcCol = Math.min(ow - 1, Math.floor(srcU * ow));
      sqClass[j * squareSize + i] = classification[srcRow * ow + srcCol]!;
    }
  }

  const roadCoverage = buildRoadCoverageTexture(
    sqClass,
    squareSize,
    squareSize,
  );

  const polygonFade = buildPolygonFadeTexture(
    sqClass,
    squareSize,
    squareSize,
    Math.max(meta.width_m, meta.height_m),
  );

  return {
    heightmap: { data: out, resolution: hm.resolution, extent: hm.extent },
    classification: { data: sqClass, width: squareSize, height: squareSize },
    roadCoverage,
    polygonFade,
  };
}

function buildRoadCoverageTexture(
  classification: Uint8Array,
  width: number,
  height: number,
): { data: Uint8Array; width: number; height: number } {
  const combined = buildRoadCoverageChannel(classification, width, height, [
    SURFACE_CODE.road,
    SURFACE_CODE.major_road,
  ]);
  const major = buildRoadCoverageChannel(classification, width, height, [
    SURFACE_CODE.major_road,
  ]);
  const data = new Uint8Array(width * height * 4);
  for (let i = 0, p = 0; i < combined.length; i++, p += 4) {
    data[p] = combined[i]!;
    data[p + 1] = major[i]!;
  }
  return { data, width, height };
}

/** Distance-to-outside feather width for the polygon fade, in metres. */
const POLYGON_FADE_FEATHER_M = 40;

function buildPolygonFadeTexture(
  classification: Uint8Array,
  width: number,
  height: number,
  extentM: number,
): { data: Uint8Array; width: number; height: number } {
  const outsideCode = SURFACE_CODE.outside;
  const outsideMask = new Uint8Array(width * height);
  let anyOutside = false;
  for (let i = 0; i < classification.length; i++) {
    if (classification[i] === outsideCode) {
      outsideMask[i] = 1;
      anyOutside = true;
    }
  }
  const data = new Uint8Array(width * height);
  if (!anyOutside) {
    data.fill(255);
    return { data, width, height };
  }
  const dist = chamfer34(outsideMask, width, height);
  const cellM = extentM / Math.max(width, height);
  const featherCells = Math.max(1, POLYGON_FADE_FEATHER_M / cellM);
  for (let i = 0; i < data.length; i++) {
    if (outsideMask[i]) {
      data[i] = 0;
      continue;
    }
    const t = Math.min(1, dist[i]! / featherCells);
    const smooth = t * t * (3 - 2 * t);
    data[i] = Math.round(smooth * 255);
  }
  return { data, width, height };
}

function buildRoadCoverageChannel(
  classification: Uint8Array,
  width: number,
  height: number,
  surfaceCodes: number | readonly number[],
): Uint8Array {
  const codes =
    typeof surfaceCodes === "number" ? [surfaceCodes] : surfaceCodes;
  const mask = new Uint8Array(width * height);
  const inverseMask = new Uint8Array(width * height);
  let any = false;
  for (let i = 0; i < classification.length; i++) {
    const c = classification[i]!;
    let hit = false;
    for (let k = 0; k < codes.length; k++) {
      if (c === codes[k]) {
        hit = true;
        break;
      }
    }
    if (hit) {
      mask[i] = 1;
      any = true;
    } else {
      inverseMask[i] = 1;
    }
  }
  const out = new Uint8Array(width * height);
  if (!any) return out;

  const outsideDist = chamfer34(mask, width, height);
  const insideDist = chamfer34(inverseMask, width, height);
  const featherPx = 4.0;
  for (let i = 0; i < out.length; i++) {
    const signedPx = mask[i] ? insideDist[i]! : -outsideDist[i]!;
    const t = Math.max(
      0,
      Math.min(1, (signedPx + featherPx) / (featherPx * 2)),
    );
    const smooth = t * t * (3 - 2 * t);
    out[i] = Math.round(smooth * 255);
  }
  return out;
}

function chamfer34(mask: Uint8Array, w: number, h: number): Float32Array {
  const INF = 1e9;
  const d = new Float32Array(w * h);
  for (let i = 0; i < d.length; i++) d[i] = mask[i] ? 0 : INF;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = y * w + x;
      let v = d[idx]!;
      if (x > 0) v = Math.min(v, d[idx - 1]! + 3);
      if (y > 0) v = Math.min(v, d[idx - w]! + 3);
      if (x > 0 && y > 0) v = Math.min(v, d[idx - w - 1]! + 4);
      if (x < w - 1 && y > 0) v = Math.min(v, d[idx - w + 1]! + 4);
      d[idx] = v;
    }
  }
  for (let y = h - 1; y >= 0; y--) {
    for (let x = w - 1; x >= 0; x--) {
      const idx = y * w + x;
      let v = d[idx]!;
      if (x < w - 1) v = Math.min(v, d[idx + 1]! + 3);
      if (y < h - 1) v = Math.min(v, d[idx + w]! + 3);
      if (x < w - 1 && y < h - 1) v = Math.min(v, d[idx + w + 1]! + 4);
      if (x > 0 && y < h - 1) v = Math.min(v, d[idx + w - 1]! + 4);
      d[idx] = v;
    }
  }
  for (let i = 0; i < d.length; i++) d[i] = d[i]! / 3;
  return d;
}
